import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, GitBranch, History, List, Loader2, Lock, Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  isMainBranch,
  useActiveBranch,
  useGetBranchQuery,
  useGetMainBranchQuery,
  useListBranchesQuery,
  useSwitchBranch,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../lib/utils'
import { BranchCreateDialog } from '../Branches/BranchCreateDialog'
import { BranchHistoryDrawer } from '../Branches/BranchHistoryDrawer'
import { Badge } from '../ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'

const BASELINE_REF = 'main'
// The switcher lists every active branch in one fetch. The registry is small,
// so this caps the fetch defensively rather than truly paging — enough to show
// every branch without letting a runaway registry unbound the menu.
const ALL_BRANCHES = 200

/**
 * Topbar branch quick-switcher. Lists every active branch in one flat list:
 * main (the protected baseline) and the active branch pinned first, then all
 * other branches — user-facing and system/ingest together, name-sorted, no
 * sections. Switching opens the branch first (commit-backed branches build
 * their working database on first open, a one-time ~seconds restore) and only
 * flips the active context once the open succeeds; main needs no open.
 */
export function BranchSelector() {
  const navigate = useNavigate()
  const { data: mainBranch } = useGetMainBranchQuery()
  // Every active branch in one query (no origin filter → all populations:
  // user-facing and system/ingest alike). Main is fetched separately and pinned.
  const { data: allData } = useListBranchesQuery({ pageSize: ALL_BRANCHES })
  const activeId = useActiveBranch()
  // Resolve the active branch by id so it is labelled correctly even when it is
  // older than the recent window (off the quick-switcher list).
  const { data: activeRecord } = useGetBranchQuery(activeId ?? '', { skip: !activeId })
  const { switchBranch, isOpening } = useSwitchBranch()
  const [createOpen, setCreateOpen] = React.useState(false)
  const [historyOpen, setHistoryOpen] = React.useState(false)

  const activeBranch = activeId ? activeRecord : undefined

  // One flat list of every active branch: main first, then the active branch
  // (if distinct), then all other branches — user-facing and system/ingest
  // together, name-sorted — deduped by id.
  const items = React.useMemo(() => {
    const byId = new Map<string, Branch>()
    if (mainBranch) byId.set(mainBranch.id, mainBranch)
    if (activeBranch && !isMainBranch(activeBranch)) byId.set(activeBranch.id, activeBranch)
    const rest = (allData?.branches ?? [])
      .filter((b) => b.status === 'active' && !byId.has(b.id))
      .sort((a, b) => a.name.localeCompare(b.name))
    for (const b of rest) byId.set(b.id, b)
    return [...byId.values()]
  }, [mainBranch, activeBranch, allData])

  // History target: the active branch, or main on the baseline (main has its
  // own commits — merges, the root).
  const historyBranch = activeBranch ?? mainBranch

  const handleSelect = async (branch: Branch) => {
    // Only main routes to the baseline (no header); a user-protected branch is
    // still its own database — switching to it is a read-only view.
    const targetId = isMainBranch(branch) ? undefined : branch.id
    if ((targetId ?? undefined) === (activeId ?? undefined)) return
    try {
      await switchBranch(targetId)
    } catch {
      toast.error(
        `Could not open ${branch.name} — staying on ${activeBranch?.name ?? BASELINE_REF}`
      )
    }
  }

  const renderItem = (branch: Branch) => {
    const isCurrent = isMainBranch(branch) ? !activeId : activeId === branch.id
    // Intake branches carry their intake in the name already; the badge only
    // marks the population so the row stays short enough to read the name.
    const originLabel = branch.origin === 'ingest' ? 'ingest' : null
    return (
      <DropdownMenuItem
        key={branch.id}
        disabled={isOpening}
        onSelect={() => void handleSelect(branch)}
        className={cn(isCurrent && 'bg-brand-soft text-brand focus:bg-brand-soft focus:text-brand')}
      >
        {branch.protected ? (
          <Lock className={cn('size-3.5 shrink-0', isCurrent ? 'text-brand' : 'text-ink-3')} />
        ) : (
          <GitBranch className={cn('size-3.5 shrink-0', isCurrent ? 'text-brand' : 'text-ink-3')} />
        )}
        <span className="truncate font-mono text-xs">{branch.name}</span>
        {originLabel ? (
          <Badge variant="secondary" className="text-10">
            {originLabel}
          </Badge>
        ) : null}
        {isCurrent ? <Check className="ml-auto size-3.5" /> : null}
      </DropdownMenuItem>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isOpening}
            aria-label="Switch branch"
            className="flex h-8 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 font-mono text-xs text-ink shadow-xs hover:bg-st-idle-bg/40 disabled:cursor-wait disabled:opacity-70"
          >
            {isOpening ? (
              <Loader2 className="size-3.5 animate-spin text-ink-3" />
            ) : (activeBranch?.protected ?? true) ? (
              // The protected affordance: main (or any protected branch) reads
              // as the locked baseline it is — edits will prompt.
              <Lock className="size-3.5 text-ink-3" />
            ) : (
              <GitBranch className="size-3.5 text-ink-3" />
            )}
            <span className="max-w-40 truncate">
              {isOpening ? 'opening…' : (activeBranch?.name ?? BASELINE_REF)}
            </span>
            <ChevronDown className="size-3.5 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[70vh] w-56 overflow-y-auto">
          {items.map(renderItem)}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => navigate('/settings?tab=branches')}>
            <List className="size-3.5 text-ink-3" />
            <span className="text-xs">View all branches…</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isOpening} onSelect={() => setCreateOpen(true)}>
            <Plus className="size-3.5 text-ink-3" />
            <span className="text-xs">Create branch…</span>
          </DropdownMenuItem>
          {historyBranch ? (
            <DropdownMenuItem onSelect={() => setHistoryOpen(true)}>
              <History className="size-3.5 text-ink-3" />
              <span className="text-xs">View history</span>
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <BranchCreateDialog open={createOpen} onOpenChange={setCreateOpen} source={activeBranch} />
      {historyBranch ? (
        <BranchHistoryDrawer
          branch={historyBranch}
          open={historyOpen}
          onOpenChange={setHistoryOpen}
        />
      ) : null}
    </>
  )
}
